/**
 * 基于角色的路由保护组件
 * 提供细粒度的权限控制
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store';
import { UserRole } from '../../store/slices/authSlice';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
  showAccessDenied?: boolean;
}

export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo,
  showAccessDenied = true,
}) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAppSelector(state => state.auth);

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      [UserRole.ADMIN]: t('common:role.admin'),
      [UserRole.EXCHANGE]: t('common:role.exchange'),
      [UserRole.ISSUER]: t('common:role.issuer'),
    };
    return roleMap[role] || role;
  };
  const location = useLocation();

  // 未登录，重定向到登录页
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // 已登录但没有用户信息（异常情况）
  if (!user) {
    console.error('User is authenticated but user data is missing');
    return <Navigate to="/auth/login" replace />;
  }

  // 检查角色权限
  const hasPermission = allowedRoles.includes(user.role as UserRole);

  if (!hasPermission) {
    // 如果指定了重定向路径
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // 显示权限不足页面
    if (showAccessDenied) {
      return (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Result
            status="403"
            title="403"
            subTitle={t('common:message.noPermission')}
            extra={
              <Button type="primary" onClick={() => window.history.back()}>
                {t('common:action.back')}
              </Button>
            }
          />
        </div>
      );
    }

    // 默认重定向到首页
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// 便捷的权限检查Hook
export const useRolePermission = () => {
  const { user, isAuthenticated } = useAppSelector(state => state.auth);

  const hasRole = (allowedRoles: UserRole[]): boolean => {
    if (!isAuthenticated || !user) return false;
    return allowedRoles.includes(user.role as UserRole);
  };

  const isAdmin = (): boolean => hasRole([UserRole.ADMIN]);
  const isExchange = (): boolean => hasRole([UserRole.EXCHANGE]);
  const isIssuer = (): boolean => hasRole([UserRole.ISSUER]);
  const isExchangeOrAdmin = (): boolean =>
    hasRole([UserRole.EXCHANGE, UserRole.ADMIN]);
  const isIssuerOrAdmin = (): boolean =>
    hasRole([UserRole.ISSUER, UserRole.ADMIN]);

  return {
    user,
    isAuthenticated,
    hasRole,
    isAdmin,
    isExchange,
    isIssuer,
    isExchangeOrAdmin,
    isIssuerOrAdmin,
  };
};

export default RoleBasedRoute;
